import { CSSProperties, useEffect, useRef } from 'react';
import styled from 'styled-components';

export type SceneContext = {
    ctx: CanvasRenderingContext2D;
    width: number;
    height: number;
    /* Seconds since the scene started. */
    time: number;
    /* Seconds since the previous frame, clamped. */
    delta: number;
    /* Canvas-local pointer position in CSS pixels, or null while not hovering. */
    mouseX: number | null;
    mouseY: number | null;
    hovering: boolean;
    /* Clicks since the previous frame, canvas-local CSS pixels. Drained every frame. */
    clicks: { x: number; y: number }[];
};

export type Scene = (frame: SceneContext) => void;
export type SceneFactory = (palette: string[], background: string) => Scene;

const Wrapper = styled.div`
    position: absolute;
    inset: 0;
    overflow: hidden;
`;

const Surface = styled.canvas<{ $interactive?: boolean }>`
    display: block;
    width: 100%;
    height: 100%;
    cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
`;

type AnimationCanvasProps = {
    scene: Scene;
    style?: CSSProperties;
    className?: string;
    /* Shows a pointer cursor to hint the scene reacts to clicks. */
    interactive?: boolean;
};

export default function AnimationCanvas({ scene, style, className, interactive }: AnimationCanvasProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const sceneRef = useRef(scene);
    sceneRef.current = scene;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let width = 0;
        let height = 0;

        const resize = () => {
            const ratio = window.devicePixelRatio || 1;
            const rect = canvas.getBoundingClientRect();
            width = Math.max(1, Math.round(rect.width));
            height = Math.max(1, Math.round(rect.height));
            canvas.width = Math.round(width * ratio);
            canvas.height = Math.round(height * ratio);
            ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        };

        resize();

        let frame = 0;
        let start = 0;
        let previous = 0;

        let mouseX: number | null = null;
        let mouseY: number | null = null;
        let hovering = false;
        let pendingClicks: { x: number; y: number }[] = [];

        const pointFromEvent = (event: MouseEvent) => {
            const rect = canvas.getBoundingClientRect();
            return { x: event.clientX - rect.left, y: event.clientY - rect.top };
        };

        const handleMove = (event: MouseEvent) => {
            const point = pointFromEvent(event);
            mouseX = point.x;
            mouseY = point.y;
            hovering = true;
        };
        const handleLeave = () => {
            mouseX = null;
            mouseY = null;
            hovering = false;
        };
        const handleClick = (event: MouseEvent) => {
            pendingClicks.push(pointFromEvent(event));
        };

        canvas.addEventListener('mousemove', handleMove);
        canvas.addEventListener('mouseleave', handleLeave);
        canvas.addEventListener('click', handleClick);

        const render = (timestamp: number) => {
            if (!start) {
                start = timestamp;
                previous = timestamp;
            }
            const time = (timestamp - start) / 1000;
            const delta = Math.min(0.1, (timestamp - previous) / 1000);
            previous = timestamp;

            const clicks = pendingClicks;
            pendingClicks = [];
            sceneRef.current({ ctx, width, height, time, delta, mouseX, mouseY, hovering, clicks });
            frame = window.requestAnimationFrame(render);
        };

        /* Nothing off screen should burn frames; the grid stacks many of these. */
        const visibility = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !frame) {
                    start = 0;
                    frame = window.requestAnimationFrame(render);
                } else if (!entry.isIntersecting && frame) {
                    window.cancelAnimationFrame(frame);
                    frame = 0;
                }
            },
            { rootMargin: '150px' }
        );
        visibility.observe(canvas);

        const resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(canvas);

        return () => {
            visibility.disconnect();
            resizeObserver.disconnect();
            canvas.removeEventListener('mousemove', handleMove);
            canvas.removeEventListener('mouseleave', handleLeave);
            canvas.removeEventListener('click', handleClick);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, []);

    return (
        <Wrapper style={style} className={className}>
            <Surface ref={canvasRef} $interactive={interactive} />
        </Wrapper>
    );
}
